import {
  UseMutationResult,
  useMutation,
  useQueryClient
} from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { useAuth } from 'user-auth'
import { graphierQL } from 'utils'
import {
  UpdateWorkspaceByIdMutation,
  UpdateWorkspaceByIdMutationVariables
} from '~workspace/types/__generated/gql/graphql'
import {
  GetWorkspaceByIdResults,
  getWorkspaceByIdQueryKey
} from './useGetWorkspaceById'

const query = /* GraphQL */ `
  mutation UpdateWorkspaceById($id: ID!, $input: UpdateWorkspaceInput!) {
    updateWorkspaceById(id: $id, input: $input) {
      id
      description
      name
      logo
      email
      twitterUsername
      websiteUrl
      communityUrl
    }
  }
`

export type UpdateWorkspaceByIdResults =
  UpdateWorkspaceByIdMutation['updateWorkspaceById']
export type UpdateWorkspaceByIdVariables = UpdateWorkspaceByIdMutationVariables

export const useUpdateWorkspaceById = (): UseMutationResult<
  UpdateWorkspaceByIdResults,
  Error,
  UpdateWorkspaceByIdVariables
> => {
  const {
    query: { workspaceId }
  } = useRouter()
  const queryClient = useQueryClient()

  const { user, setAuth } = useAuth()

  return useMutation({
    mutationKey: ['updateWorkspaceById'],
    mutationFn: (variables: UpdateWorkspaceByIdVariables) =>
      graphierQL<UpdateWorkspaceByIdMutation, UpdateWorkspaceByIdVariables>({
        query,
        useToken: true,
        variables
      }).then((res) => res.updateWorkspaceById),
    onSuccess: (data) => {
      queryClient.setQueryData<GetWorkspaceByIdResults>(
        getWorkspaceByIdQueryKey(String(workspaceId)),
        (prev) => {
          if (!prev) return
          return { ...prev, ...data }
        }
      )

      if (user)
        setAuth({
          user: {
            ...user,
            membershipCollection: {
              ...user.membershipCollection,
              items: user.membershipCollection.items.map((mem) =>
                mem.workspace.id === data.id
                  ? { ...mem, workspace: { ...mem.workspace, ...data } }
                  : mem
              )
            }
          }
        })
    }
  })
}
